import { type SyntheticEvent, useCallback, useRef, useState } from 'react';

type ControllableStateOptions<T> = {
  prop?: T;
  defaultProp: T;
  onChange?: (value: T) => void;
};

/**
 * Manages controlled vs uncontrolled state for a component.
 * When `prop` is provided, the component is controlled externally.
 * When only `defaultProp` is provided, the component manages its own state.
 */
export function useControllableState<T>({
  prop,
  defaultProp,
  onChange,
}: ControllableStateOptions<T>): [T, (next: T) => void] {
  const [internal, setInternal] = useState(defaultProp);
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : internal;
  const valueRef = useRef(value);
  valueRef.current = value;

  const setValue = useCallback(
    (next: T) => {
      if (Object.is(valueRef.current, next)) return;

      if (!isControlled) {
        setInternal(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  return [value, setValue];
}

type DisabledStateOptions = {
  disabled?: boolean;
  focusable?: boolean;
};

/**
 * Returns props that implement the aria-disabled pattern, keeping elements
 * focusable for screen readers while suppressing interaction.
 */
export function useDisabledState({
  disabled = false,
  focusable = true,
}: DisabledStateOptions = {}) {
  const suppressEvent = useRef((e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  });

  if (!disabled) {
    return {};
  }

  const props: Record<string, unknown> = {
    'aria-disabled': true,
    onClick: suppressEvent.current,
    onKeyDown: suppressEvent.current,
  };

  if (!focusable) {
    props.tabIndex = -1;
  }

  return props;
}

export { useScrollLock } from './useScrollLock';
export { useMediaQuery } from './useMediaQuery';
export { useContainerWidthAbove } from './useContainerWidthAbove';
export { useDebounce } from './useDebounce';
export { useBackgroundInert } from './useBackgroundInert';
export { useIsDesktop } from './useIsDesktop';
export { useViewMode } from './useViewMode';
export { BELOW_FOLD_ACTIVATION_ROOT_MARGIN, useViewportActivation } from './useViewportActivation';
export type { ViewMode } from './useViewMode';
